import { useEffect, useState } from "react";
import api from "../lib/api";
import { Link } from "react-router-dom";
import {
  Package,
  Flask,
  Drop,
  Warning,
  ListNumbers,
  Factory,
  ArrowRight,
  ShoppingBag,
} from "@phosphor-icons/react";

const TYPE_LABEL = {
  materia_prima: "Materia Prima",
  semilavorato: "Semilavorato",
  prodotto_finito: "Prodotto Finito",
};

const MOV_LABEL = {
  carico: { label: "Carico", cls: "bg-[#E6F4F1] text-[#2A9D8F] border-[#2A9D8F]/20" },
  scarico: { label: "Scarico", cls: "bg-[#FBEDEE] text-[#E63946] border-[#E63946]/20" },
  produzione: { label: "Produzione", cls: "bg-[#F3F4F6] text-[#1D3557] border-[#1D3557]/20" },
  inventario: { label: "Inventario", cls: "bg-[#FEF5EB] text-[#F4A261] border-[#F4A261]/20" },
};

export default function Dashboard() {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get("/dashboard/stats").then((r) => setStats(r.data)).catch(() => setStats(null));
  }, []);

  const kpis = [
    {
      label: "Materie Prime",
      value: stats?.counts?.materia_prima ?? 0,
      icon: Drop,
      accent: "#457B9D",
      tid: "kpi-materia-prima",
    },
    {
      label: "Semilavorati",
      value: stats?.counts?.semilavorato ?? 0,
      icon: Flask,
      accent: "#1D3557",
      tid: "kpi-semilavorato",
    },
    {
      label: "Prodotti Finiti",
      value: stats?.counts?.prodotto_finito ?? 0,
      icon: Package,
      accent: "#2A9D8F",
      tid: "kpi-prodotto-finito",
    },
    {
      label: "Batch Attivi",
      value: stats?.active_batches ?? 0,
      icon: Factory,
      accent: "#F4A261",
      tid: "kpi-batch-attivi",
    },
  ];

  return (
    <div className="p-8 lg:p-12 fade-up" data-testid="dashboard-page">
      <div className="mb-10">
        <div className="text-[10px] uppercase tracking-[0.2em] text-[#4B5563] mb-2">
          Pannello di controllo
        </div>
        <h1 className="font-display text-4xl lg:text-5xl font-bold tracking-tight text-[#0A0A0A]">
          Dashboard
        </h1>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-10">
        {kpis.map((k) => (
          <div
            key={k.label}
            data-testid={k.tid}
            className="bg-white border border-[#E5E7EB] p-6 hover:shadow-sm transition-shadow"
          >
            <div className="flex items-start justify-between mb-6">
              <div className="text-[10px] uppercase tracking-[0.15em] font-semibold text-[#4B5563]">
                {k.label}
              </div>
              <k.icon size={20} weight="duotone" color={k.accent} />
            </div>
            <div className="font-display font-bold text-5xl tracking-tight text-[#0A0A0A]">
              {k.value}
            </div>
            <div className="mt-2 font-mono-data text-[10px] uppercase tracking-wider text-[#4B5563]">
              unità registrate
            </div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div
          className="bg-white border border-[#E5E7EB] lg:col-span-1"
          data-testid="low-stock-panel"
        >
          <div className="flex items-center justify-between p-6 border-b border-[#E5E7EB]">
            <div className="flex items-center gap-2">
              <Warning size={18} weight="bold" color="#E63946" />
              <h2 className="font-display font-semibold uppercase tracking-wider text-xs text-[#0A0A0A]">
                Scorte Basse
              </h2>
            </div>
            <span className="font-mono-data text-xs text-[#4B5563]">
              {stats?.low_stock?.length || 0}
            </span>
          </div>
          {stats?.low_stock?.length ? (
            <ul>
              {stats.low_stock.map((p) => (
                <li
                  key={p.id}
                  className="p-4 border-b border-[#E5E7EB] last:border-b-0 flex items-center justify-between"
                >
                  <div>
                    <div className="text-sm font-medium text-[#0A0A0A]">{p.name}</div>
                    <div className="font-mono-data text-[10px] text-[#4B5563]">
                      {p.barcode}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-mono-data text-sm text-[#E63946] font-semibold">
                      {p.stock} / {p.min_stock} {p.unit}
                    </div>
                    <div className="text-[10px] uppercase tracking-wider text-[#4B5563]">
                      {TYPE_LABEL[p.type]}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <div className="p-8 text-center text-sm text-[#4B5563]">
              Nessuna scorta sotto la soglia.
            </div>
          )}
        </div>

        <div className="bg-white border border-[#E5E7EB] lg:col-span-2" data-testid="recent-movements-panel">
          <div className="flex items-center justify-between p-6 border-b border-[#E5E7EB]">
            <div className="flex items-center gap-2">
              <ListNumbers size={18} weight="bold" color="#1D3557" />
              <h2 className="font-display font-semibold uppercase tracking-wider text-xs text-[#0A0A0A]">
                Movimenti Recenti
              </h2>
            </div>
            <Link
              to="/movimenti"
              className="text-[10px] uppercase tracking-wider font-semibold text-[#1D3557] hover:text-[#2A4B7C] flex items-center gap-1"
              data-testid="view-all-movements-link"
            >
              Tutti i movimenti <ArrowRight size={12} weight="bold" />
            </Link>
          </div>
          {stats?.recent_movements?.length ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-[#F9FAFB] border-b border-[#E5E7EB]">
                  <tr className="text-left text-[10px] uppercase tracking-wider text-[#4B5563]">
                    <th className="px-4 py-3 font-semibold">Tipo</th>
                    <th className="px-4 py-3 font-semibold">Prodotto</th>
                    <th className="px-4 py-3 font-semibold">Codice</th>
                    <th className="px-4 py-3 font-semibold text-right">Quantità</th>
                    <th className="px-4 py-3 font-semibold text-right">Stock</th>
                  </tr>
                </thead>
                <tbody>
                  {stats.recent_movements.map((m) => {
                    const meta = MOV_LABEL[m.type] || MOV_LABEL.carico;
                    return (
                      <tr key={m.id} className="border-b border-[#E5E7EB] hover:bg-[#F9FAFB]">
                        <td className="px-4 py-3">
                          <span
                            className={`inline-block px-2 py-1 text-[10px] font-semibold uppercase tracking-wider border ${meta.cls}`}
                          >
                            {meta.label}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-[#0A0A0A]">{m.product_name}</td>
                        <td className="px-4 py-3 font-mono-data text-xs text-[#4B5563]">
                          {m.product_barcode}
                        </td>
                        <td className="px-4 py-3 text-right font-mono-data text-sm text-[#0A0A0A]">
                          {m.quantity > 0 ? "+" : ""}
                          {m.quantity} {m.unit}
                        </td>
                        <td className="px-4 py-3 text-right font-mono-data text-sm text-[#4B5563]">
                          {m.stock_after} {m.unit}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="p-8 text-center text-sm text-[#4B5563]">
              Nessun movimento registrato.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
